import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';

import {
  EXTRA_LINE_ITEMS,
  LINE_ITEM_ANNUAL_PASS_VOUCHER,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_LISTING_CONTINGENT,
  LINE_ITEM_NIGHT,
} from '../../../util/types';
import { passCoversWholeStay, passIncludesExtraLineItem } from './passCoverage';

const { Money } = sdkTypes;

/**
 * The night line items that make up the stay itself — one per contiguous price
 * run. A refunded booking carries a second, negative night item that
 * LineItemUnitsRefundMaybe renders as the refund row; it is not a price run.
 *
 * @param {Array} lineItems all line items of the transaction
 * @param {'customer'|'provider'} userRole role the breakdown is rendered for
 * @returns {Array} the night line items belonging to the stay
 */
const stayNightItems = (lineItems, userRole) =>
  lineItems.filter(
    (item) =>
      item.code === LINE_ITEM_NIGHT &&
      item.includeFor.includes(userRole) &&
      !item.reversal &&
      item.lineTotal.amount >= 0
  );

/** @returns {Object} a copy of the line item with both prices at zero */
const free = (item) => ({
  ...item,
  unitPrice: new Money(0, item.unitPrice.currency),
  lineTotal: new Money(0, item.lineTotal.currency),
});

/**
 * Applies the display-only price adjustments of the booking breakdown to a
 * transaction's line items.
 *
 * Several cases make parts of a booking free without the backend removing them
 * from the transaction: a provider whose listing contingent already paid for
 * the nights, a customer whose annual pass covers the whole stay (nights,
 * annual pass voucher and nightly commission), and a customer whose pass
 * includes an extra line item. The rows, the subtotal and the total all have to
 * agree on that, so every adjustment is made once here instead of by each row
 * component during its own render.
 *
 * The returned array is new and every adjusted item is a copy — the transaction
 * and its line items are left untouched. Items that are not adjusted are passed
 * through as they are.
 *
 * The real charge is computed server-side in `server/api-util/lineItems/`; this
 * is display only.
 *
 * @param {Object} transaction transaction (or estimated transaction) with line items and booking
 * @param {'customer'|'provider'} userRole role the breakdown is rendered for
 * @param {Object|null} pass the current user's annual pass, if any
 * @param {boolean} [hasValidPass=false] whether that pass is valid right now
 * @returns {Array} line items to display the breakdown with
 */
export const adjustLineItemsForDisplay = (transaction, userRole, pass, hasValidPass = false) => {
  const lineItems = transaction.attributes.lineItems;
  const isCustomer = userRole === 'customer';

  // The listing contingent already covers the nights, so the provider is not
  // paid per night. Only the nightly price is free of charge; the line total
  // stays as it is and is settled by the contingent line item.
  const contingentCoversNights =
    userRole === 'provider' &&
    !!lineItems.find((item) => item.code === LINE_ITEM_LISTING_CONTINGENT && !item.reversal);

  const passCoversStay = isCustomer && passCoversWholeStay(transaction, pass);

  const freeNightItems =
    contingentCoversNights || passCoversStay
      ? new Set(stayNightItems(lineItems, userRole))
      : new Set();

  // Only the first match of each is zeroed, as the row components did with
  // their `find`.
  const freeVoucherItem = passCoversStay
    ? lineItems.find((item) => item.code === LINE_ITEM_ANNUAL_PASS_VOUCHER && !item.reversal)
    : null;
  const freeCommissionItem = passCoversStay
    ? lineItems.find(
        (item) =>
          item.code === LINE_ITEM_CUSTOMER_COMMISSION &&
          !item.reversal &&
          item.lineTotal.amount > 0
      )
    : null;

  const isFreeExtraLineItem = (item) =>
    EXTRA_LINE_ITEMS.includes(item.code) &&
    item.includeFor.includes(userRole) &&
    item.lineTotal.amount >= 0 &&
    passIncludesExtraLineItem(item, pass, hasValidPass);

  let changed = false;

  const adjusted = lineItems.map((item) => {
    if (freeNightItems.has(item)) {
      changed = true;
      return {
        ...item,
        unitPrice: new Money(0, item.unitPrice.currency),
        // A contingent settles the line total through its own line item, so
        // only the nightly price reads as free there.
        ...(passCoversStay ? { lineTotal: new Money(0, item.lineTotal.currency) } : {}),
      };
    }

    if (item === freeVoucherItem || item === freeCommissionItem || isFreeExtraLineItem(item)) {
      changed = true;
      return free(item);
    }

    return item;
  });

  // Nothing to adjust — hand back the very same array so the memoised rows do
  // not re-render for a new array of unchanged items.
  return changed ? adjusted : lineItems;
};
